import React, { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import {
  List,
  Typography,
  Skeleton,
} from "antd";
import { useTenantNavigation } from "../../hooks/useTenantNavigation.js";
import { useTranslation } from "react-i18next";
import RequestRow from "./RequestRow.jsx";
import { useRequestWorkflows } from "../../hooks/useRequestWorkflows.js";
import { getAllSpacesRequests } from "../../services/requestServices.js";
import { getLocalFormattedDateWithoutTime } from "../../utils/getLocalFormattedDate.js";
const { Title } = Typography;

const ApprovalsAwaited = (props: any) => {
  const { t } = useTranslation();
  const [data, setData] = useState([])
  const [loader, setLoader] = useState(false)
  const { userInfo } = useSelector((state: any) => state.user)
  const navigate = useTenantNavigation();
  // Holds the step translations, so a language switch only re-renders the rows.
  const workflows: Record<string, any> = useRequestWorkflows(data);

  // Get email from localStorage first, fallback to redux
  const getEmail = useCallback(() => {
    const localEmail = localStorage.getItem("userEmail");
    return localEmail || userInfo?.email || "";
  }, [userInfo?.email]);

  const fetchSpacesRequests = useCallback(async () => {
    const email = getEmail();
    if (!email) {
      setLoader(false);
      return;
    }
    setLoader(true)

    let requestsResponse = await getAllSpacesRequests(email);
    if (requestsResponse.data && Array.isArray(requestsResponse.data)) {
      let filteredData: any = requestsResponse.data || []
      filteredData = filteredData.map((data:any) => {
        return {...data, createdOn: getLocalFormattedDateWithoutTime(data.createdOn) }
      })
      setData(filteredData)
    }
    setLoader(false) // Set loader to false after data is fetched
  }, [userInfo, getEmail]);

  useEffect(() => {
    fetchSpacesRequests();

  }, []);
  const navigateToRequest = (item: any) => {
    navigate(`/app/space/${item.spaceId}/approve/${item.workflowId}/${item.requestId}`)
  }

  // Create skeleton loading component that matches RequestRow structure
  const SkeletonRequestRow = () => (
    <div className="w-full  py-0 sm:px-4 sm:py-2 border-b border-gray-100 last:border-b-0 cursor-pointer hover:bg-gray-50 transition-colors duration-200">
      {/* Mobile Layout */}
      <div className="md:hidden space-y-1">
        <div className="flex justify-between items-start gap-2">
          <div className="flex-1 min-w-0">
            <Skeleton.Input style={{ width: 120, height: 14 }} active size="small" />
            <div className="mt-1">
              <Skeleton.Input style={{ width: 200, height: 16 }} active size="small" />
            </div>
          </div>
          <div className="flex-shrink-0">
            <Skeleton.Button style={{ width: 60, height: 18, padding: '1px 6px', fontSize: '10px' }} active size="small" />
          </div>
        </div>
        <div className="flex justify-between items-center pt-1">
          <Skeleton.Input style={{ width: 80, height: 12 }} active size="small" />
          <Skeleton.Input style={{ width: 60, height: 12 }} active size="small" />
        </div>
      </div>

      {/* Desktop Layout */}
      <div className="hidden md:grid md:grid-cols-12 md:gap-3 md:items-center md:min-h-[32px]">
        <div className="col-span-6">
          <div className="space-y-1">
            <Skeleton.Input style={{ width: 100, height: 12 }} active size="small" />
            <Skeleton.Input style={{ width: 250, height: 16 }} active size="small" />
          </div>
        </div>
        <div className="col-span-3">
          <Skeleton.Button style={{ width: 70, height: 20 }} active size="small" />
        </div>
        <div className="col-span-3 text-right">
          <Skeleton.Input style={{ width: 80, height: 14 }} active size="small" />
        </div>
      </div>
    </div>
  );

  return (
    <div className="w-full max-w-7xl mx-auto px-3 py-0 sm:p-4 lg:p-1">
      <div className="bg-white rounded-lg ">
        {/* Header */}


        {/* Desktop Table Header - Hidden on mobile */}
        <div className="hidden md:block px-4 sm:px-6 py-3  border-b border-gray-200">
          <div className="grid grid-cols-12 gap-4 text-xs font-medium text-gray-700 uppercase tracking-wider">
            <div className="col-span-3">{t("Request ID")}</div>
            <div className="col-span-3">{t("Status")}</div>
            <div className="col-span-3">{t("Due Date")}</div>
            <div className="col-span-3">{t("Creation Date")}</div>
          </div>
        </div>

        {/* Content */}
        <div className="divide-y divide-gray-200">
          {loader ? (
            // Show skeleton loading when data is being fetched
            <div>
              {Array.from({ length: 7 }).map((_, index) => (
                <SkeletonRequestRow key={index} />
              ))}
            </div>
          ) : (
            <List
              className="w-full [&_.ant-list-item]:!p-0"
              pagination={{
                position: 'bottom',
                align: 'center',
                pageSize: 6,
                showSizeChanger: false,
                showQuickJumper: false,
                className: "px-4 sm:px-6 py-2",
                responsive: true,
                size: 'small'
              }}
              dataSource={data}
              renderItem={(item: any) => (
                <List.Item className="px-0 py-0 border-0">
                  <RequestRow {...item} navigateToRequest={navigateToRequest} workflow={workflows[item.workflowId]} />
                </List.Item>
              )}
            />
          )}
        </div>

      </div>
    </div>
  )
}

export default ApprovalsAwaited